import React from 'react';
import { Subscription } from '@/services/types/subscriptionTypes';
import { SubscriptionStatusBadge } from './SubscriptionStatusBadge';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Badge } from '@/components/ui/badge';
import { 
  Filter, Eye, Play, Ban, X, Trash2
} from 'lucide-react';

interface ActionProps {
  handleViewSubscription: (id: string) => void;
  handleStatusUpdate: (id: string, status: 'active' | 'cancelled' | 'paused') => void;
  handleDeleteSubscription: (id: string) => void;
}

// Форматирование даты
const formatDate = (dateString: string | Date | null | undefined) => {
  if (!dateString) return '-';
  try {
    const dateValue = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return format(dateValue, 'dd.MM.yyyy', { locale: ru });
  } catch {
    return String(dateString);
  }
};

// Получение последнего блока UUID (после последнего дефиса)
const getLastUUIDBlock = (uuid: string): string => {
  if (!uuid) return '';
  const parts = uuid.split('-');
  return parts.length > 0 ? parts[parts.length - 1] : uuid;
};

// Интервал подписки
const getIntervalLabel = (interval: string) => {
  const intervalMap: { [key: string]: string } = {
    'day': 'День',
    'week': 'Неделя',
    'month': 'Месяц',
    'year': 'Год'
  };
  return intervalMap[interval] || interval;
};

export const getSubscriptionColumns = ({ 
  handleViewSubscription, 
  handleStatusUpdate,
  handleDeleteSubscription 
}: ActionProps) => [
  {
    key: 'id',
    header: 'ID',
    cell: (subscription: Subscription) => {
      const id = getLastUUIDBlock(subscription.id);
      return <span className="font-mono text-xs">{id}</span>;
    },
  },
  {
    key: 'rental_number',
    header: 'Номер аренды',
    cell: (subscription: Subscription) => {
      return <span>
        {subscription.rentals?.human_number || '-'}
      </span>;
    },
  },
  {
    key: 'courier',
    header: 'Курьер',
    cell: (subscription: Subscription) => {
      if (!subscription.rentals) return <span className="text-muted-foreground">Не указан</span>;
      
      // При доступе к courier учитываем разные структуры данных
      const courierName = subscription.courierName || 
                          subscription.rentals.courier?.name || 
                          subscription.rentals.courier_id || 
                          'Не указан';
      
      return <span>{courierName}</span>;
    },
  },
  {
    key: 'payment_method',
    header: 'Реквизиты карты',
    cell: (subscription: Subscription) => {
      return (subscription.payment_method_id || subscription.cardLast4 || subscription.card_last4) ? (
        <Badge variant="success" className="font-medium text-xs">Есть</Badge>
      ) : (
        <Badge variant="destructive" className="font-medium text-xs">Нет</Badge>
      );
    },
  },
  {
    key: 'status',
    header: 'Статус',
    cell: (subscription: Subscription) => {
      return <SubscriptionStatusBadge status={subscription.status} />;
    },
    sortable: true,
  },
  {
    key: 'interval',
    header: 'Периодичность',
    cell: (subscription: Subscription) => {
      const intervalMap: { [key: string]: string } = {
        'day': 'День',
        'week': 'Неделя',
        'month': 'Месяц',
        'year': 'Год'
      };
      return <span>{intervalMap[subscription.interval || ''] || subscription.interval}</span>;
    },
    sortable: true,
  },
  {
    key: 'amount',
    header: 'Сумма',
    cell: (subscription: Subscription) => {
      return <span>{subscription.amount} ₽</span>;
    },
    sortable: true,
  },
  {
    key: 'start_date',
    header: 'Дата начала',
    cell: (subscription: Subscription) => {
      return <span>{formatDate(subscription.startDate || subscription.start_date)}</span>;
    },
    sortable: true,
  },
  {
    key: 'next_payment_date',
    header: 'Следующий платеж',
    cell: (subscription: Subscription) => {
      return <span>{formatDate(subscription.nextPaymentDate || subscription.next_payment_date)}</span>;
    },
    sortable: true,
  },
  {
    key: 'actions',
    header: 'Действия',
    cell: (subscription: Subscription) => {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <span className="sr-only">Открыть меню</span>
              <Filter className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Действия</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => handleViewSubscription(subscription.id)}>
              <Eye className="mr-2 h-4 w-4" />
              <span>Просмотреть</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            
            {subscription.status !== 'active' && (
              <DropdownMenuItem onClick={() => handleStatusUpdate(subscription.id, 'active')}>
                <Play className="mr-2 h-4 w-4" />
                <span>Активировать</span>
              </DropdownMenuItem>
            )}
            
            {subscription.status !== 'paused' && subscription.status !== 'cancelled' && (
              <DropdownMenuItem onClick={() => handleStatusUpdate(subscription.id, 'paused')}>
                <Ban className="mr-2 h-4 w-4" />
                <span>Приостановить</span>
              </DropdownMenuItem>
            )}
            
            {subscription.status !== 'cancelled' && (
              <DropdownMenuItem onClick={() => handleStatusUpdate(subscription.id, 'cancelled')}>
                <X className="mr-2 h-4 w-4" />
                <span>Отменить</span>
              </DropdownMenuItem>
            )}
            
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={() => handleDeleteSubscription(subscription.id)}
              className="text-red-600"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              <span>Удалить</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
